import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Typography,
} from "@mui/material";

import {
  AddShoppingCart,
  ArrowForward,
  CheckCircle,
  Close,
  DeleteOutline,
  School,
  ShoppingCart,
} from "@mui/icons-material";

// ============================================================
// CHANGE THESE TWO IMPORT PATHS IF YOUR FOLDER STRUCTURE
// IS DIFFERENT
// ============================================================

import { GetCourseList, GetUserCourses } from "src/handler/coursehandler";
import courseImage from "src/Common/CourseImage";
import UserLoader from "../Dashboard/UserLoader";

// ============================================================
// INTERFACES
// ============================================================

interface Course {
  courseId: number;
  courseName: string;
  courseDescription: string;
  packageId: number;
  coursePrice?: number | string;
  price?: number | string;
  salePrice?: number | string;
  amount?: number | string;
  images?: string;
  isSubscribed: boolean;
}

interface CourseListResponse {
  success: boolean;
  data?: {
    storeCourseList: Course[];
  };
  message?: string;
  errors?: {
    errorLabel?: string;
    errorMessage?: string;
  };
  statusCode?: number;
}

// ============================================================
// COMPONENT
// ============================================================

const CourseStore: React.FC = () => {
  // ==========================================================
  // STATES
  // ==========================================================

  const [courseList, setCourseList] = useState<Course[]>([]);

  const [selectedCourses, setSelectedCourses] = useState<Course[]>(
    []
  );

  const [loading, setLoading] = useState<boolean>(true);

  const [paymentLoading, setPaymentLoading] =
    useState<boolean>(false);

  const [error, setError] = useState<string>("");

  const [snackbarOpen, setSnackbarOpen] =
    useState<boolean>(false);

  const [snackbarMessage, setSnackbarMessage] =
    useState<string>("");

  
  const [cartOpen, setCartOpen] = useState(false);
// ==========================================================
  // GET PACKAGE ID FROM LOCAL STORAGE
  // ==========================================================

  const getPackageId = (): number => {
    try {
        debugger
      const profileString = localStorage.getItem("userProfile");

      if (!profileString) {
        return 0;
      }

      const profile = JSON.parse(profileString);

      return Number(profile?.packageId || 0);
    } catch (error) {
      console.error("Error reading userProfile:", error);

      return 0;
    }
  };

  // ==========================================================
  // LOAD COURSES
  // ==========================================================

  const loadCourses = async () => {
    try {
        debugger
      setLoading(true);
      setError("");

      const packageId = getPackageId();

      console.log("Course Store Package ID:", packageId);

      const response: CourseListResponse = await GetUserCourses(packageId,true);

      console.log("Course Store Response:", response);

      if (response?.success) {
        setCourseList(
          response?.data?.storeCourseList || []
        );
      } else {
        setCourseList([]);

        setError(
          response?.message ||
            response?.errors?.errorMessage ||
            "Unable to load courses."
        );
      }
    } catch (error: any) {
      console.error("Course Store API Error:", error);

      setCourseList([]);

      setError(
        error?.message ||
          "Something went wrong while loading courses."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    loadCourses();
  }, []);

  // ==========================================================
  // CHECK COURSE SELECTED
  // ==========================================================

  const isCourseSelected = (courseId: number): boolean => {
    return selectedCourses.some(
      (course) => course.courseId === courseId
    );
  };

  // ==========================================================
  // ADD / REMOVE COURSE
  // ==========================================================

  const toggleCourse = (course: Course) => {
    // Don't allow already purchased courses
    if (course.isSubscribed) {
      return;
    }

    const alreadySelected = isCourseSelected(
      course.courseId
    );

    if (alreadySelected) {
      setSelectedCourses((previous) =>
        previous.filter(
          (item) => item.courseId !== course.courseId
        )
      );

      return;
    }

    setSelectedCourses((previous) => [
      ...previous,
      course,
    ]);

    setSnackbarMessage(
      `${course.courseName} added to cart`
    );

    setSnackbarOpen(true);
  };

  // ==========================================================
  // REMOVE FROM CART
  // ==========================================================

  const removeCourse = (courseId: number) => {
    setSelectedCourses((previous) =>
      previous.filter(
        (course) => course.courseId !== courseId
      )
    );
  };

  // ==========================================================
  // CLEAR CART
  // ==========================================================

  const clearCart = () => {
    setSelectedCourses([]);
  };

  // ==========================================================
  // CART COUNT
  // ==========================================================

  const cartCount = selectedCourses.length;

  // ==========================================================
  // GRAND TOTAL
  // ==========================================================

  const getCoursePrice = (course: Course): number => {
    const value =
      course.coursePrice ??
      course.salePrice ??
      course.price ??
      course.amount ??
      0;

    return Number(value) || 0;
  };

  const grandTotal = useMemo(
    () => selectedCourses.reduce((total, course) => total + getCoursePrice(course), 0),
    [selectedCourses]
  );

  // ==========================================================
  // FORMAT PRICE
  // ==========================================================

  const formatPrice = (price: number): string => {
    return Number(price || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    });
  };

  // ==========================================================
  // PROCEED TO PAYMENT
  // ==========================================================

  const handleProceedToPayment = async () => {
    if (selectedCourses.length === 0) {
      setSnackbarMessage(
        "Please select at least one course."
      );

      setSnackbarOpen(true);

      return;
    }

    setPaymentLoading(true);

    try {
      const paymentData = {
        courses: selectedCourses.map((course) => ({
          courseId: course.courseId,
          courseName: course.courseName,
          coursePrice: getCoursePrice(course),
        })),

        courseIds: selectedCourses.map(
          (course) => course.courseId
        ),

        totalAmount: grandTotal,
      };

      console.log(
        "Course Payment Data:",
        paymentData
      );

      // ======================================================
      // YOUR RAZORPAY CODE WILL GO HERE
      // ======================================================

      /*
      
      Example:

      const response = await CreateCoursePayment({
        courseIds: paymentData.courseIds,
        amount: paymentData.totalAmount
      });

      Then open Razorpay.

      ======================================================
      */

      // Temporary loader test
      await new Promise((resolve) =>
        setTimeout(resolve, 1000)
      );

      setSnackbarMessage(
        "Ready for payment integration."
      );

      setSnackbarOpen(true);
    } catch (error) {
      console.error("Payment Error:", error);

      setSnackbarMessage(
        "Unable to proceed with payment."
      );

      setSnackbarOpen(true);
    } finally {
      setPaymentLoading(false);
    }
  };

  // ==========================================================
  // LOADING SCREEN
  // ==========================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background:
            "radial-gradient(circle at top, rgba(99, 102, 241, 0.18), transparent 42%), linear-gradient(135deg, #070b16 0%, #0b1020 100%)",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={45}
            sx={{
              color: "#00d9ff",
            }}
          />

          <Typography
            sx={{
              color: "#b8c5d6",
              mt: 2,
            }}
          >
            Loading courses...
          </Typography>
        </Box>
      </Box>
    );
  }

  // ==========================================================
  // MAIN PAGE
  // ==========================================================

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at 85% 10%, rgba(99, 102, 241, 0.20), transparent 32%), radial-gradient(circle at 10% 90%, rgba(6, 182, 212, 0.12), transparent 30%), linear-gradient(135deg, #070b16 0%, #0b1020 48%, #12102a 100%)",
        color: "#fff",
        py: {
          xs: 3,
          md: 5,
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          px: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        {/* ==================================================
            HEADER
        ================================================== */}

        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  fontSize: {
                    xs: "28px",
                    sm: "34px",
                    md: "40px",
                  },
                  background:
                    "linear-gradient(90deg, #00d9ff, #8b5cf6)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Course Store
              </Typography>

              <Typography
                sx={{
                  color: "#9aa8ba",
                  mt: 1,
                  fontSize: {
                    xs: "14px",
                    md: "16px",
                  },
                }}
              >
                Expand your learning journey with
                additional courses.
              </Typography>
            </Box>

            {/* CART BUTTON */}
            <Button
              onClick={() => setCartOpen(true)}
              startIcon={<ShoppingCart />}
              sx={{
                color: "#fff",
                px: 2,
                py: 1.2,
                borderRadius: 3,
                textTransform: "none",
                background: "rgba(0, 217, 255, 0.07)",
                border: "1px solid rgba(0, 217, 255, 0.2)",
                "&:hover": {
                  background: "rgba(0, 217, 255, 0.14)",
                  borderColor: "#00d9ff",
                },
                "& .MuiButton-startIcon": { color: "#00d9ff" },
              }}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography sx={{ color: "#8d9bad", fontSize: "12px" }}>
                  Your Cart
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                  {cartCount} {cartCount === 1 ? "course" : "courses"}
                </Typography>
              </Box>
            </Button>
          </Box>
        </Box>

        {/* ==================================================
            ERROR
        ================================================== */}

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: 2,
            }}
          >
            {error}
          </Alert>
        )}

        {/* ==================================================
            NO COURSES
        ================================================== */}

        {!error && courseList.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: {
                xs: 4,
                md: 7,
              },
              textAlign: "center",
              background:
                "rgba(255,255,255,0.03)",
              border:
                "1px solid rgba(255,255,255,0.08)",
              borderRadius: 4,
            }}
          >
            <School
              sx={{
                fontSize: 60,
                color: "#536174",
                mb: 2,
              }}
            />

            <Typography
              variant="h6"
              sx={{
                color: "#fff",
                fontWeight: 700,
              }}
            >
              No courses available
            </Typography>

            <Typography
              sx={{
                color: "#8793a5",
                mt: 1,
              }}
            >
              There are currently no additional
              courses available for purchase.
            </Typography>
          </Paper>
        )}

        {/* ==================================================
            COURSE LIST
        ================================================== */}

        {courseList.length > 0 && (
          <Grid
            container
            spacing={3}
            sx={{ pb: 3 }}
          >
            {courseList.map((course) => {
              const selected =
                isCourseSelected(course.courseId);

              const poster = courseImage.find(
                (item) => item.courseId === course.courseId
              )?.posterLink;

              return (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  lg={3}
                  key={course.courseId}
                >
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      position: "relative",
                      overflow: "hidden",

                      background: selected
                        ? "linear-gradient(145deg, rgba(0,217,255,0.12), rgba(139,92,246,0.12))"
                        : "rgba(255,255,255,0.035)",

                      border: selected
                        ? "1px solid #00d9ff"
                        : "1px solid rgba(255,255,255,0.08)",

                      borderRadius: 4,

                      boxShadow: selected
                        ? "0 0 25px rgba(0,217,255,0.10)"
                        : "none",

                      transition:
                        "all 0.25s ease",

                      "&:hover": {
                        transform:
                          "translateY(-4px)",

                        borderColor:
                          course.isSubscribed
                            ? "rgba(255,255,255,0.08)"
                            : "#00d9ff",
                      },
                    }}
                  >
                    {/* BADGE */}

                    {course.isSubscribed ? (
                      <Chip
                        icon={
                          <CheckCircle
                            sx={{
                              color:
                                "#65e6a5 !important",
                            }}
                          />
                        }
                        label="Already Purchased"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          zIndex: 2,
                          color: "#65e6a5",
                          background:
                            "rgba(101,230,165,0.10)",
                          border:
                            "1px solid rgba(101,230,165,0.25)",
                          fontWeight: 600,
                        }}
                      />
                    ) : selected ? (
                      <Chip
                        icon={
                          <CheckCircle
                            sx={{
                              color:
                                "#00d9ff !important",
                            }}
                          />
                        }
                        label="Selected"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          right: 12,
                          zIndex: 2,
                          color: "#00d9ff",
                          background:
                            "rgba(0,217,255,0.12)",
                          border:
                            "1px solid rgba(0,217,255,0.3)",
                          fontWeight: 700,
                        }}
                      />
                    ) : null}

                    <CardContent
                      sx={{
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      {/* COURSE POSTER */}
                      <Box
                        sx={{
                          width: "100%",
                          height: 168,
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 2.5,
                          mb: 2,
                          background: "linear-gradient(135deg, #172554, #312e81)",
                          border: "1px solid rgba(129,140,248,0.22)",
                        }}
                      >
                        {poster ? (
                          <Box
                            component="img"
                            src={poster}
                            alt={course.courseName}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                              transition: "transform 0.35s ease",
                              ".MuiCard-root:hover &": { transform: "scale(1.05)" },
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background:
                                "radial-gradient(circle at 30% 20%, rgba(34,211,238,0.22), transparent 45%), linear-gradient(135deg, #172554, #312e81)",
                            }}
                          >
                            <School sx={{ color: "#a5b4fc", fontSize: 46 }} />
                          </Box>
                        )}
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            background:
                              "linear-gradient(180deg, transparent 45%, rgba(7,11,22,0.58) 100%)",
                            pointerEvents: "none",
                          }}
                        />
                      </Box>

                      {/* COURSE NAME */}

                      <Typography
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: "18px",
                          lineHeight: 1.35,
                          minHeight: 49,
                          mb: 1,
                        }}
                      >
                        {course.courseName}
                      </Typography>

                      {/* DESCRIPTION */}

                      <Typography
                        sx={{
                          color: "#8d9bad",
                          fontSize: "14px",
                          lineHeight: 1.6,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: 67,
                          mb: 2,
                        }}
                      >
                        {course.courseDescription ||
                          "Learn this course and expand your skills."}
                      </Typography>

                      <Box sx={{ flexGrow: 1 }} />

                      <Divider
                        sx={{
                          borderColor:
                            "rgba(255,255,255,0.08)",
                          mb: 2,
                        }}
                      />

                      {/* PRICE */}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#8793a5",
                            fontSize: "13px",
                          }}
                        >
                          Course Price
                        </Typography>

                        <Typography
                          sx={{
                            color: "#00d9ff",
                            fontWeight: 800,
                            fontSize: "21px",
                          }}
                        > {"\u20B9"}{formatPrice(getCoursePrice(course))}
                        </Typography>
                      </Box>

                      {/* BUTTON */}

                      <Button
                        fullWidth
                        variant={
                          selected
                            ? "outlined"
                            : "contained"
                        }
                        disabled={
                          course.isSubscribed
                        }
                        onClick={() =>
                          toggleCourse(course)
                        }
                        startIcon={
                          course.isSubscribed ? (
                            <CheckCircle />
                          ) : selected ? (
                            <CheckCircle />
                          ) : (
                            <AddShoppingCart />
                          )
                        }
                        sx={{
                          minHeight: 45,
                          borderRadius: 2.5,
                          textTransform: "none",
                          fontWeight: 700,

                          ...(selected && {
                            color: "#00d9ff",
                            borderColor:
                              "#00d9ff",
                          }),

                          ...(!selected &&
                            !course.isSubscribed && {
                              background:
                                "linear-gradient(90deg, #00d9ff, #8b5cf6)",
                              color: "#fff",
                            }),

                          ...(course.isSubscribed && {
                            color: "#65e6a5",
                            borderColor:
                              "rgba(101,230,165,0.3)",
                            background:
                              "rgba(101,230,165,0.05)",
                          }),
                        }}
                      >
                        {course.isSubscribed
                          ? "Already Purchased"
                          : selected
                          ? "Remove from Cart"
                          : "Add to Cart"}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}



      </Container>

      {/* FULL-SCREEN CART */}
      <Dialog
        fullScreen
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: {
            background: "radial-gradient(circle at 85% 10%, rgba(99, 102, 241, 0.20), transparent 32%), radial-gradient(circle at 10% 90%, rgba(6, 182, 212, 0.12), transparent 30%), linear-gradient(135deg, #070b16 0%, #0b1020 48%, #12102a 100%)",
            color: "#fff",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 4 }, py: 2, display: "flex", alignItems: "center",
            justifyContent: "space-between", borderBottom: "1px solid rgba(255,255,255,0.08)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ShoppingCart sx={{ color: "#00d9ff" }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Your Cart</Typography>
              <Typography sx={{ color: "#8d9bad", fontSize: "14px" }}>
                {cartCount} {cartCount === 1 ? "course" : "courses"} selected
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setCartOpen(false)} aria-label="Close cart" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>

        <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
          {selectedCourses.length === 0 ? (
            <Paper elevation={0} sx={{ p: { xs: 4, md: 7 }, textAlign: "center", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 4 }}>
              <ShoppingCart sx={{ fontSize: 56, color: "#536174", mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Your cart is empty</Typography>
              <Typography sx={{ color: "#8793a5", mt: 1 }}>Add a course from the store to see it here.</Typography>
              <Button onClick={() => setCartOpen(false)} sx={{ mt: 3, color: "#00d9ff", textTransform: "none" }}>Continue Shopping</Button>
            </Paper>
          ) : (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {selectedCourses.map((course) => (
                  <Paper key={course.courseId} elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, background: "rgba(255,255,255,0.035)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3 }}>
                    <Box sx={{ width: 48, height: 48, minWidth: 48, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,217,255,0.08)" }}>
                      <School sx={{ color: "#00d9ff" }} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>{course.courseName}</Typography>
                      <Typography sx={{ color: "#8d9bad", fontSize: "14px", mt: 0.5 }}> {"\u20B9"}{formatPrice(getCoursePrice(course))}</Typography>
                    </Box>
                    <IconButton onClick={() => removeCourse(course.courseId)} aria-label={`Remove ${course.courseName} from cart`} sx={{ color: "#8793a5", "&:hover": { color: "#ff6b6b", background: "rgba(255,107,107,0.08)" } }}>
                      <DeleteOutline />
                    </IconButton>
                  </Paper>
                ))}
              </Box>

              <Paper elevation={0} sx={{ mt: 3, p: { xs: 2, sm: 2.5 }, background: "rgba(0,217,255,0.06)", border: "1px solid rgba(0,217,255,0.2)", borderRadius: 3 }}>
                <Box sx={{ display: "flex", alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                  <Box>
                    <Typography sx={{ color: "#8995a7", fontSize: "14px" }}>Grand Total</Typography>
                    <Typography sx={{ fontWeight: 900, fontSize: "28px" }}> {"\u20B9"}{formatPrice(grandTotal)}</Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Button onClick={clearCart} sx={{ color: "#b8c5d6", textTransform: "none" }}>Clear Cart</Button>
                    <Button variant="contained" onClick={handleProceedToPayment} disabled={paymentLoading} endIcon={!paymentLoading && <ArrowForward />} sx={{ minHeight: 48, px: 3, borderRadius: 2.5, textTransform: "none", fontWeight: 800, background: "linear-gradient(90deg, #00d9ff, #8b5cf6)" }}>
                      {paymentLoading ? "Processing..." : "Proceed to Payment"}
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          )}
        </Container>
      </Dialog>

      {/* ==================================================
          USER LOADER
      ================================================== */}

      {paymentLoading && <UserLoader />}

      {/* ==================================================
          SNACKBAR
      ================================================== */}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2500}
        onClose={() =>
          setSnackbarOpen(false)
        }
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() =>
            setSnackbarOpen(false)
          }
          severity="success"
          variant="filled"
          sx={{
            width: "100%",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CourseStore;
